import { useEffect, useState } from 'react';

import { SATELLITE_CLASS_LABELS, SATELLITE_KIND_LABELS } from '../data/satelliteEnrich';
import type { RichSatellite } from '../data/types';
import { useStore } from '../store';
import { cls } from '../lib/format';

const SATELLITE_ICON_BASE = '/icons/satellites/';

interface Props {
  satellite: RichSatellite | null;
  selectedSatId: string | null;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-bg-raised p-2 ring-1 ring-bg-border">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-slate-100">{value}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-right text-slate-200">{v}</dd>
    </div>
  );
}

export function SatellitePanel({ satellite, selectedSatId }: Props) {
  const open = useStore((s) => s.panelOpen);
  const setOpen = useStore((s) => s.setPanelOpen);
  const followSat = useStore((s) => s.followSat);
  const setFollowSat = useStore((s) => s.setFollowSat);

  const [iconUrl, setIconUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!satellite) {
      setIconUrl(null);
      return;
    }
    setIconUrl(`${SATELLITE_ICON_BASE}${satellite.kind}.svg`);
  }, [satellite?.kind, satellite?.noradId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!selectedSatId) return null;

  if (!satellite) {
    return (
      <>
        {open ? (
          <div className="fixed inset-0 z-40 bg-black/30 sm:hidden" onClick={() => setOpen(false)} />
        ) : null}
        <section
          className={cls(
            'fixed right-0 z-50 flex w-full flex-col overflow-hidden border-l border-bg-border bg-bg-panel/95 backdrop-blur transition-transform duration-200',
            'bottom-0 h-[68vh] max-h-[68vh] rounded-t-2xl sm:top-0 sm:h-full sm:max-h-full sm:max-w-md sm:rounded-none',
            open ? 'translate-y-0 sm:translate-x-0' : 'translate-y-full sm:translate-y-0 sm:translate-x-full',
          )}
          style={{ paddingBottom: 'var(--safe-bottom)' }}
        >
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-sm text-slate-400">
            <div className="h-8 w-8 animate-pulse rounded-full bg-bg-raised" />
            Loading satellite…
          </div>
        </section>
      </>
    );
  }

  const kindLabel = SATELLITE_KIND_LABELS[satellite.kind] ?? satellite.kind;

  return (
    <>
      {open ? (
        <div className="fixed inset-0 z-40 bg-black/30 sm:hidden" onClick={() => setOpen(false)} />
      ) : null}
      <section
        className={cls(
          'fixed right-0 z-50 flex w-full flex-col overflow-hidden border-l border-bg-border bg-bg-panel/95 backdrop-blur transition-transform duration-200',
          'bottom-0 h-[68vh] max-h-[68vh] rounded-t-2xl sm:top-0 sm:h-full sm:max-h-full sm:max-w-md sm:rounded-none',
          open ? 'translate-y-0 sm:translate-x-0' : 'translate-y-full sm:translate-y-0 sm:translate-x-full',
        )}
        style={{ paddingBottom: 'var(--safe-bottom)' }}
        aria-label="Satellite details"
      >
        <div className="flex shrink-0 items-center justify-between px-4 pt-3">
          <div className="mx-auto h-1.5 w-10 rounded-full bg-bg-border sm:hidden" />
          <button
            onClick={() => setOpen(false)}
            className="ml-auto rounded-md bg-bg-raised px-2.5 py-1 text-xs text-slate-300"
          >
            Close
          </button>
        </div>

        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y px-4 pb-6">
          <div className="mt-1 flex items-center gap-3">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt=""
                className="h-12 w-12 rounded-lg bg-bg-raised object-contain p-1 ring-1 ring-bg-border"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-bg-raised text-lg ring-1 ring-bg-border">
                🛰
              </div>
            )}
            <div className="min-w-0">
              <div className="truncate text-lg font-semibold text-slate-100">{satellite.name}</div>
              <div className="truncate text-sm text-slate-400">{kindLabel}</div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <Stat label="Altitude" value={`${Math.round(satellite.altitudeKm)} km`} />
            <Stat label="Speed" value={`${satellite.speedKmS.toFixed(2)} km/s`} />
            <Stat label="Inclination" value={`${satellite.inclinationDeg.toFixed(1)}°`} />
            <Stat label="Period" value={`${satellite.periodMin.toFixed(1)} min`} />
          </div>

          <dl className="mt-4 space-y-1.5 text-sm">
            <Row k="NORAD ID" v={satellite.noradId} />
            <Row k="Object ID" v={satellite.objectId || '—'} />
            <Row k="Class" v={SATELLITE_CLASS_LABELS[satellite.satelliteClass] ?? satellite.satelliteClass} />
            <Row k="Kind" v={kindLabel} />
            <Row k="Orbit regime" v={satellite.regime} />
            <Row k="Owner" v={satellite.ownerCountry ?? '—'} />
            <Row k="Classification" v={satellite.classification} />
            <Row k="TLE age" v={`${satellite.ageDays.toFixed(1)} days`} />
          </dl>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setFollowSat(!followSat)}
              className={cls(
                'rounded-lg px-3 py-2 text-sm ring-1',
                followSat ? 'bg-accent/20 text-accent ring-accent/40' : 'bg-bg-raised text-slate-200 ring-bg-border',
              )}
            >
              {followSat ? 'Following' : 'Follow satellite'}
            </button>
            <a
              href={`https://celestrak.org/NORAD/elements/gp.php?CATNR=${satellite.noradId}&FORMAT=TLE`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg bg-bg-raised px-3 py-2 text-sm text-accent ring-1 ring-bg-border hover:bg-white/5"
            >
              CelesTrak TLE
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
